import { entityDef } from '../config/balancing';
import type { EntityKind, ParkState } from '../types';
import { upgradeCost } from './economy';
import { hasBuiltKind } from './unlocks';

/**
 * Post-tutorial park goal. First match wins.
 * "Built" is `hasBuiltKind`. Prices are read from live balancing on each call.
 */
export type DerivedGoalId =
  | 'starter'
  | 'snack'
  | 'drink'
  | 'spinning'
  | 'starter_upgrade'
  | 'pirate'
  | 'ferris';

export interface DerivedParkGoal {
  id: DerivedGoalId;
  kind: EntityKind;
  /** Live build price, or the live cost to upgrade the placed Starter once. */
  price: number;
  /** Coins still short of `price`. Reaching zero does not complete the goal. */
  gap: number;
}

function priced(id: DerivedGoalId, kind: EntityKind, price: number, coins: number): DerivedParkGoal {
  return {
    id,
    kind,
    price,
    gap: Math.max(0, price - coins),
  };
}

function purchase(kind: EntityKind): number {
  return entityDef(kind).purchaseCost;
}

export function deriveParkGoal(state: ParkState): DerivedParkGoal {
  if (!hasBuiltKind(state, 'starter_ride')) {
    return priced('starter', 'starter_ride', purchase('starter_ride'), state.coins);
  }
  if (!hasBuiltKind(state, 'snack_kiosk')) {
    return priced('snack', 'snack_kiosk', purchase('snack_kiosk'), state.coins);
  }
  if (!hasBuiltKind(state, 'drink_kiosk')) {
    return priced('drink', 'drink_kiosk', purchase('drink_kiosk'), state.coins);
  }
  if (!hasBuiltKind(state, 'spinning_ride')) {
    return priced('spinning', 'spinning_ride', purchase('spinning_ride'), state.coins);
  }
  if (!hasBuiltKind(state, 'pirate_ship')) {
    const starter = state.entities.find((entity) => entity.kind === 'starter_ride' && entity.level < 2);
    const upgrade = starter ? upgradeCost(starter.kind, starter.level) : null;
    if (starter && upgrade != null) {
      return priced('starter_upgrade', 'starter_ride', upgrade, state.coins);
    }
    return priced('pirate', 'pirate_ship', purchase('pirate_ship'), state.coins);
  }
  return priced('ferris', 'ferris_wheel', purchase('ferris_wheel'), state.coins);
}
